import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SiteShell } from "@/components/layout/SiteShell";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { TID } from "@/constants/testIds";
import { PasswordInput, isPasswordValid } from "@/components/forms/PasswordInput";
import { api, formatApiError } from "@/lib/api";
import { KeyRound, CheckCircle2 } from "lucide-react";

const THEMES = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "Follow system" },
];

const COLORBLIND = [
  { value: "off", label: "Off" },
  { value: "protanopia", label: "Protanopia" },
  { value: "deuteranopia", label: "Deuteranopia" },
  { value: "tritanopia", label: "Tritanopia" },
];

export default function SettingsPage() {
  const { user, updateProfile } = useAuth();
  const { prefs, updatePrefs } = useTheme();
  const [saving, setSaving] = useState(false);
  const [local, setLocal] = useState(prefs);

  useEffect(() => setLocal(prefs), [prefs]);

  const change = (patch) => {
    const next = { ...local, ...patch };
    setLocal(next);
    updatePrefs(patch); // apply immediately for great UX
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile({ preferences: local });
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SiteShell>
      <section className="container-page py-14 max-w-3xl" data-testid={TID.settings.root}>
        <h1 className="font-serif text-4xl tracking-tight">Settings</h1>
        <p className="mt-2 text-muted-foreground">
          Personalise your Last Bookmark experience. Changes preview live and save to your account.
        </p>

        <div className="mt-10 card-soft space-y-6">
          <h2 className="font-serif text-2xl">Appearance</h2>

          <Row label="Theme" hint="Choose a light, dark, or system-matching theme.">
            <select
              data-testid={TID.settings.theme}
              value={local.theme}
              onChange={(e) => change({ theme: e.target.value })}
              className="sb-input max-w-xs"
            >
              {THEMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Row>
        </div>

        <div className="mt-6 card-soft space-y-6">
          <h2 className="font-serif text-2xl">Accessibility</h2>
          <Toggle
            label="Larger text"
            hint="Increases base font size across the app for easier reading."
            checked={local.large_text}
            onChange={(v) => change({ large_text: v })}
            testid={TID.settings.largeText}
          />
          <Toggle
            label="High contrast"
            hint="Boosts contrast between text and backgrounds."
            checked={local.high_contrast}
            onChange={(v) => change({ high_contrast: v })}
            testid={TID.settings.highContrast}
          />
          <Toggle
            label="Reduce motion"
            hint="Minimises animations and transitions."
            checked={local.reduce_motion}
            onChange={(v) => change({ reduce_motion: v })}
            testid={TID.settings.reduceMotion}
          />
          <Row label="Colorblind mode" hint="Adjust colour palette for colour-vision differences.">
            <select
              data-testid={TID.settings.colorblind}
              value={local.colorblind_mode}
              onChange={(e) => change({ colorblind_mode: e.target.value })}
              className="sb-input max-w-xs"
            >
              {COLORBLIND.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Row>
        </div>

        <div className="mt-6 card-soft space-y-6">
          <h2 className="font-serif text-2xl">Notifications</h2>
          <Toggle
            label="Email notifications"
            hint="Occasional updates about your projects, messages and applications."
            checked={local.notifications_email}
            onChange={(v) => change({ notifications_email: v })}
            testid={TID.settings.emailNotif}
          />
        </div>

        <EmailPreferencesCard />
        <BlockedUsersCard />
        <ChangePasswordCard />

        <div className="mt-8 flex justify-end gap-3">
          <button className="btn-primary" onClick={save} disabled={saving} data-testid={TID.settings.save}>
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>

        <p className="mt-8 text-xs text-muted-foreground">
          Signed in as {user?.email}. Roles: {(user?.roles || []).join(", ") || "—"}
        </p>
      </section>
    </SiteShell>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
      <div>
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        data-testid={testid}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? "bg-[hsl(var(--sb-forest))]" : "bg-muted"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-[hsl(var(--sb-cream))] shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}


function EmailPreferencesCard() {
  const { user, updateProfile } = useAuth();
  const current = user?.profile?.email_updates_opt_in !== false; // default true
  const [optedIn, setOptedIn] = useState(current);
  const [saving, setSaving] = useState(false);

  useEffect(() => setOptedIn(current), [current]);

  const toggle = async (next) => {
    setOptedIn(next);
    setSaving(true);
    try {
      await updateProfile({ profile: { ...user.profile, email_updates_opt_in: next } });
      toast.success(next ? "You're opted in to email updates." : "You've opted out of email updates.");
    } catch (e) {
      setOptedIn(!next);
      toast.error("Could not save preference. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 card-soft space-y-4" data-testid="settings-email-preferences-card">
      <h2 className="font-serif text-2xl">Email Preferences</h2>
      <label className="flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          className="mt-1 w-4 h-4 rounded border-border accent-[hsl(var(--sb-forest))]"
          checked={optedIn}
          disabled={saving}
          onChange={(e) => toggle(e.target.checked)}
          data-testid="settings-email-opt-in-toggle"
        />
        <span className="text-sm leading-relaxed">
          Receive email updates about Last Bookmark, new features, bug fixes,
          maintenance, and important announcements.
        </span>
      </label>
      <p className="text-xs text-muted-foreground">
        You can change this at any time. We&apos;ll never share your email with third parties.
      </p>
      <p className="text-xs text-muted-foreground" data-testid="settings-essential-email-note">
        Essential project emails, such as application decisions and required steps like a project&apos;s
        confidentiality agreement, are always sent so you don&apos;t miss them.
      </p>
    </div>
  );
}


function ChangePasswordCard() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [justSucceeded, setJustSucceeded] = useState(false);

  const reset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Please enter your current password.");
      return;
    }
    if (!isPasswordValid(newPassword)) {
      toast.error("Please meet every password requirement below.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match.");
      return;
    }
    if (newPassword === currentPassword) {
      toast.error("New password must be different from your current password.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      reset();
      setJustSucceeded(true);
      toast.success("Password updated");
      // Hide the success banner after a few seconds
      setTimeout(() => setJustSucceeded(false), 5000);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 card-soft space-y-5" data-testid="settings-change-password-card">
      <div className="flex items-center gap-2">
        <KeyRound size={20} className="text-[hsl(var(--sb-forest-2))]" />
        <h2 className="font-serif text-2xl">Change password</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        Update the password you use to sign in. You&apos;ll stay logged in on this device.
      </p>

      {justSucceeded && (
        <div
          className="rounded-xl border border-[hsl(var(--sb-forest)/0.3)] bg-[hsl(var(--sb-forest)/0.08)] text-[hsl(var(--sb-forest-2))] px-4 py-3 text-sm inline-flex items-center gap-2"
          data-testid="settings-change-password-success"
          role="status"
        >
          <CheckCircle2 size={16} /> Your password has been updated.
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <PasswordInput
          value={currentPassword}
          onChange={setCurrentPassword}
          label="Current password"
          placeholder="Your existing password"
          required
          autoComplete="current-password"
          testId="settings-current-password"
        />
        <PasswordInput
          value={newPassword}
          onChange={setNewPassword}
          label="New password"
          placeholder="Choose a strong password"
          required
          autoComplete="new-password"
          showRules
          testId="settings-new-password"
          confirmValue={confirmPassword}
          onConfirmChange={setConfirmPassword}
          confirmLabel="Confirm new password"
          confirmTestId="settings-confirm-password"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={reset}
            disabled={saving || (!currentPassword && !newPassword && !confirmPassword)}
            className="btn-ghost"
            data-testid="settings-change-password-clear"
          >
            Clear
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary"
            data-testid="settings-change-password-submit"
          >
            {saving ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}

function BlockedUsersCard() {
  // Shows every user the caller has blocked, with a one-click unblock. The
  // backend is the source of truth — every action here re-fetches the list
  // so the UI can never lie about who's currently blocked.
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/moderation/blocks");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const unblock = async (userId) => {
    setBusyId(userId);
    try {
      await api.delete(`/moderation/blocks/${userId}`);
      toast.success("User unblocked. They can now message you again.");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  return (
    <div
      className="mt-6 card-soft space-y-3"
      data-testid="settings-blocked-users-card"
    >
      <div>
        <h2 className="font-serif text-2xl">Blocked Users</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Blocked users can’t send you messages or Coffee Chat requests. They
          are not notified when you block them. Existing conversations stay
          visible to you.
        </p>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading\u2026</p>
      ) : rows.length === 0 ? (
        <p
          className="text-sm text-muted-foreground"
          data-testid="settings-blocked-users-empty"
        >
          You haven’t blocked anyone.
        </p>
      ) : (
        <ul
          className="divide-y divide-border/60"
          data-testid="settings-blocked-users-list"
        >
          {rows.map((row) => {
            const userId = row.blocked_user_id;
            const u = row.user || {};
            return (
              <li
                key={userId}
                className="py-2.5 flex items-center justify-between gap-3"
                data-testid={`settings-blocked-row-${userId}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {u.display_name || u.email || "Unknown user"}
                  </div>
                  {row.created_at && (
                    <div className="text-xs text-muted-foreground">
                      Blocked {new Date(row.created_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn-ghost border text-sm"
                  disabled={busyId === userId}
                  onClick={() => unblock(userId)}
                  data-testid={`settings-blocked-unblock-${userId}`}
                >
                  {busyId === userId ? "Unblocking\u2026" : "Unblock"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

